/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChronoUnit, LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { filter, keys, map, sortBy } from 'es-toolkit/compat';

import { adjustDateForCutOff, businessDayTimeCutOff } from 'utils/constants';
import { getTimeFormatter } from '@tock/i18n';
import { memoizeWithTimezoneGuard } from '@tock/shared/utils/timezone';

export interface TimeOption {
  isDefault: boolean;
  key: string;
  label: string;
  value: string;
}

export const ONE_YEAR_MILLISECONDS = 1000 * 60 * 60 * 24 * 365;

export const timesForOfferedTimesAndDate = memoizeWithTimezoneGuard(
  (offeredTimes: any, selectedDate: any) => {
    return map(
      sortBy(keys(offeredTimes), (time) => {
        return time <= businessDayTimeCutOff ? `1${time}` : `0${time}`;
      }),
      (time) => ({
        value: time,
        dateTime: adjustDateForCutOff(selectedDate, time),
        seatingAreaIds: offeredTimes[time].seatingAreaIds,
      })
    );
  }
);

export const isAfterDateTime = (datetime: string) => {
  return LocalDateTime.parse(datetime).isBefore(LocalDateTime.now());
};

export const noon: LocalTime = LocalTime.of(12, 0, 0, 0);
export const isAtNoon = (datetime: string): boolean => {
  return noon.equals(LocalDateTime.parse(datetime).toLocalTime());
};

export const get15MinTimeIntervals = (
  date: string,
  startTimeSpecified: LocalDateTime,
  endTimeSpecified?: LocalDateTime
) => getTimeIntervals(date, startTimeSpecified, endTimeSpecified, 15);

export function getTimeIntervals(
  date: string,
  startTimeSpecified: LocalDateTime,
  endTimeSpecified?: LocalDateTime,
  intervalMinutes = 15
): LocalDateTime[] {
  if (endTimeSpecified && startTimeSpecified.isAfter(endTimeSpecified)) {
    throw new Error('Start time must be before end time');
  }
  let startTime = startTimeSpecified;
  const endTime = endTimeSpecified ? endTimeSpecified : startTime.plusDays(1);
  const timeIntervals: LocalDateTime[] = [];
  while (startTime.isBefore(endTime)) {
    timeIntervals.push(startTime);
    startTime = startTime.plusMinutes(intervalMinutes);
  }

  return timeIntervals;
}

export function getDefaultDateTimeChoice(
  now: LocalDateTime,
  defaultTime?: LocalTime
): LocalDateTime {
  if (defaultTime) {
    return now.toLocalTime().isBefore(defaultTime)
      ? now.toLocalDate().atTime(defaultTime)
      : now.truncatedTo(ChronoUnit.HOURS).plusHours(1);
  }

  const fivePm = LocalTime.parse('17:00');
  return now.toLocalTime().isBefore(fivePm)
    ? now.withHour(19).truncatedTo(ChronoUnit.HOURS)
    : now.truncatedTo(ChronoUnit.HOURS).plusHours(1);
}

export function getLocalDateTimesAfterNow(
  selectedDate: LocalDate,
  now: LocalTime,
  intervals: 15 | 30 = 15
): LocalDateTime[] {
  let localDateTimesAfterNow = getTimeIntervals(
    selectedDate.toString(),
    selectedDate.atTime(LocalTime.MIDNIGHT),
    undefined,
    intervals
  );
  if (selectedDate.equals(LocalDate.now())) {
    localDateTimesAfterNow = filter(localDateTimesAfterNow, function (time) {
      return time.toLocalTime().isAfter(now);
    });
  }
  return localDateTimesAfterNow;
}

export function getDisplayedTimes(
  selectedDate: LocalDate,
  now: LocalTime,
  locale: string,
  intervals: 15 | 30 = 15
): TimeOption[] {
  let displayedTimes = map(
    getTimeIntervals(
      selectedDate.toString(),
      selectedDate.atTime(LocalTime.MIDNIGHT),
      undefined,
      intervals
    ),
    (time: LocalDateTime) => {
      const timeString = time.toLocalTime().toString();
      return {
        isDefault: false,
        key: timeString,
        label: getTimeFormatter(locale).format(time.toLocalTime()),
        value: timeString,
      };
    }
  );
  if (selectedDate.equals(LocalDate.now())) {
    displayedTimes = filter(displayedTimes, function (time) {
      return LocalTime.parse(time.value).isAfter(now);
    });
  }
  return displayedTimes;
}

export function toDateTimeString(date: string, time: string) {
  return `${date}T${time}`;
}

export function toJSDate(date: LocalDate): Date {
  return new Date(Date.UTC(date.year(), date.monthValue() - 1, date.dayOfMonth()));
}

/**
 * looks at local date time and sets the time param
 * exported for testing
 */
export const getTimeParam = (now: LocalTime): string => {
  if (now.isBefore(LocalTime.parse('10:00'))) {
    return '10:00';
  } else if (now.isAfter(LocalTime.parse('10:00')) && now.isBefore(LocalTime.parse('15:00'))) {
    return '15:00';
  } else {
    return '19:00';
  }
};

export const getEndOfMonth = (month: number, year: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

export function compareLocalDate(a: LocalDate, b: LocalDate) {
  return a.compareTo(b);
}
